import { useEffect, useState } from 'react';
import { T, type Trilingual } from './i18n.ts';
import { api } from './api.ts';

/**
 * /partners「常用平台」那一页：老板的邀请链接（2026-10-29 给的）。
 *
 * ── 三条规矩（有测试盯着，见 partners-copy.test.ts） ──
 * 1. **页面顶上写明这些是邀请链接、我们会拿返佣**。不写明的话用户发现了会觉得被坑，对一个做风控的产品伤害最大。
 * 2. **只从页脚、账号页、侧栏「工具」和 GMGN Key 教程进来**。不放进提醒、熔断、利润锁死、冷静期这些地方：刚被熔断拦下的人该看到「歇一会儿」，
 *    不该看到「去别的平台开个户」。
 * 3. 链接一律是平台的官方域名、https。币安原来给的是 bsmkweb.net，它跳到 binance.com —— 直接放官方地址，
 *    一个不认识的域名看起来像钓鱼链接，而我们自己在提醒用户小心钓鱼。
 */

export interface Partner {
  id: string;
  name: string;
  url: string;
  kind: Trilingual;
  what: Trilingual;
}

/**
 * 2026-10-30 起这份列表在数据库里（partner_link），老板在运维后台「常用平台」页加、改、删；页面从 /api/public/partners 读。
 * 这里留一份和迁移 0037 写进去的一样的，接口拿不到时（断网、游客页先画出来）先用它
 */
export const PARTNERS: readonly Partner[] = [
  {
    id: 'gmgn', name: 'GMGN', url: 'https://gmgn.ai/r/CvavFle4?chain=bsc',
    kind: T('链上 Meme 交易', '鏈上 Meme 交易', 'On-chain meme trading'),
    what: T('多链 Meme 币看盘和交易。我们的浏览器扩展就是读你在 GMGN 上的钱包数据，模拟交易扩展也是在 GMGN 页面上用的。',
      '多鏈 Meme 幣看盤和交易。我們的瀏覽器擴充功能就是讀你在 GMGN 上的錢包數據，模擬交易擴充功能也是在 GMGN 頁面上用的。',
      'Multi-chain meme token charts and trading. Our browser extension reads your wallet data from GMGN, and the paper-trading extension runs on GMGN pages.')
  },
  {
    id: 'axiom', name: 'Axiom', url: 'https://axiom.trade/@sansu68',
    kind: T('链上 Meme 交易', '鏈上 Meme 交易', 'On-chain meme trading'),
    what: T('链上 Meme 币交易平台。', '鏈上 Meme 幣交易平台。', 'An on-chain meme token trading platform.')
  },
  {
    id: 'fomo', name: 'fomo', url: 'https://fomo.family/r/Sansu9',
    kind: T('链上 Meme 交易', '鏈上 Meme 交易', 'On-chain meme trading'),
    what: T('链上 Meme 币交易平台。', '鏈上 Meme 幣交易平台。', 'An on-chain meme token trading platform.')
  },
  {
    id: 'debot', name: 'DeBot', url: 'https://debot.ai/r/311813',
    kind: T('链上 Meme 交易', '鏈上 Meme 交易', 'On-chain meme trading'),
    what: T('和 GMGN 同类的链上 Meme 币交易平台。', '和 GMGN 同類的鏈上 Meme 幣交易平台。', 'An on-chain meme token trading platform similar to GMGN.')
  },
  {
    id: 'binance', name: 'Binance', url: 'https://www.binance.com/register?ref=KCPMCFL6',
    kind: T('交易所', '交易所', 'Exchange'),
    what: T('币安交易所。', '幣安交易所。', 'The Binance exchange.')
  },
  {
    id: 'binanceWallet', name: 'Binance Wallet', url: 'https://web3.binance.com/referral?ref=IARGZY8V',
    kind: T('链上钱包', '鏈上錢包', 'On-chain wallet'),
    what: T('币安的链上钱包。', '幣安的鏈上錢包。', 'Binance’s on-chain wallet.')
  },
  {
    id: 'okx', name: 'OKX', url: 'https://www.okx.com/join/7765476',
    kind: T('交易所', '交易所', 'Exchange'),
    what: T('OKX 交易所。', 'OKX 交易所。', 'The OKX exchange.')
  }
];

/** 允许出现在这一页上的域名（规矩 3）。新加一个平台要先加到这里 */
export const PARTNER_HOSTS: ReadonlySet<string> = new Set(['gmgn.ai', 'axiom.trade', 'fomo.family', 'debot.ai', 'www.binance.com', 'web3.binance.com', 'www.okx.com']);

export const PT = {
  kicker: T('常用平台', '常用平台', 'Platforms'),
  h1: T('我们自己在用的平台', '我們自己在用的平台', 'Platforms we use'),
  disclosure: T('下面都是交易风控卫士的邀请链接：你通过它注册，平台会给我们返佣。不影响你在平台上的任何费用，也不是使用交易风控卫士的条件。',
    '下面都是交易風控衛士的邀請連結：你通過它註冊，平台會給我們返佣。不影響你在平台上的任何費用，也不是使用交易風控衛士的條件。',
    'These are TradeGuard referral links: if you sign up through one, the platform pays us a commission. It does not change any of your fees there and is not a condition for using TradeGuard.'),
  caution: T('交易风控卫士只读数据、只发提醒，不碰你在这些平台上的钱，也不替你下单。在哪个平台交易都请先给自己设好纪律。',
    '交易風控衛士只讀數據、只發提醒，不碰你在這些平台上的錢，也不替你下單。在哪個平台交易都請先給自己設好紀律。',
    'TradeGuard only reads data and sends alerts; it never touches your money on these platforms or trades for you. Whichever you use, set your rules first.'),
  open: T('打开', '打開', 'Open'),
  nav: T('常用平台', '常用平台', 'Platforms')
} as const;

/** 从接口读启用中的那几条（运营在后台改的）；拿不到就用上面那一份 */
export function usePartners(): readonly Partner[] {
  const [list, setList] = useState<readonly Partner[]>(PARTNERS);
  useEffect(() => {
    let alive = true;
    void api.get<{ partners: Partner[] }>('/public/partners').then(r => {
      if (alive && r.ok && Array.isArray(r.data?.partners) && r.data.partners.length > 0) setList(r.data.partners);
    });
    return () => { alive = false; };
  }, []);
  return list;
}
